import { CommonModule } from '@angular/common';
import { Component, Inject } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { FormsModule } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { MaterialModule } from '../../material.module';
import { AppendNoteResponseDto, ProjectDetailsService } from '../../services/project-details.service';
import { SlurryService } from '../../services/slurry.service';

export interface AppendNoteDialogData {
  projectId?: number;
  slurryId?: number;
  testInputMasterId?: number;
  mode?: 'project' | 'slurry' | 'test' | 'local';
  originalNote: string;
}

@Component({
  selector: 'app-append-note-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, MaterialModule],
  templateUrl: './append-note-dialog.component.html',
  styleUrls: ['./append-note-dialog.component.css']
})
export class AppendNoteDialogComponent {
  noteToAppend = '';
  isWorking = false;
  errorMessage = '';

  get formattedOldNote(): string {
    return this.data.originalNote ?? '';
  }

  constructor(
    private readonly projectDetailsService: ProjectDetailsService,
    private readonly slurryService: SlurryService,
    private readonly dialogRef: MatDialogRef<AppendNoteDialogComponent>,
    @Inject(MAT_DIALOG_DATA) public readonly data: AppendNoteDialogData
  ) { }

  private get isSlurryMode(): boolean {
    return this.data.mode === 'slurry';
  }

  private get isLocalMode(): boolean {
    return this.data.mode === 'local';
  }

  private get isTestMode(): boolean {
    return this.data.mode === 'test';
  }

  onAppend(): void {
    const note = this.noteToAppend;
    if (!note) {
      return;
    }

    if (this.isLocalMode) {
      const existing = this.data.originalNote ?? '';
      const updatedNotes = existing ? `${note}\n${existing}` : note;
      this.dialogRef.close({ updatedNotes });
      return;
    }

    this.errorMessage = '';
    this.isWorking = true;

    const request$ = this.isTestMode
      ? this.slurryService.appendTestNote(this.data.slurryId ?? 0, this.data.testInputMasterId ?? 0, note)
      : this.isSlurryMode
        ? this.slurryService.appendSlurryNote(this.data.slurryId ?? 0, note)
        : this.projectDetailsService.appendProjectNote(this.data.projectId ?? 0, note);

    request$.subscribe({
      next: (response) => this.handleSuccess(response),
      error: (error: HttpErrorResponse) => {
        this.errorMessage = this.getErrorMessage(error, 'Failed to append note. Please try again.');
        this.isWorking = false;
      }
    });
  }

  onClear(): void {
    const shouldClear = window.confirm('Are you sure you want to clear all old Notes?');
    if (!shouldClear) {
      return;
    }

    if (this.isLocalMode) {
      this.dialogRef.close({ updatedNotes: '' });
      return;
    }

    this.errorMessage = '';
    this.isWorking = true;

    const request$ = this.isTestMode
      ? this.slurryService.clearTestNote(this.data.slurryId ?? 0, this.data.testInputMasterId ?? 0)
      : this.isSlurryMode
        ? this.slurryService.clearSlurryNote(this.data.slurryId ?? 0)
        : this.projectDetailsService.clearProjectNote(this.data.projectId ?? 0);

    request$.subscribe({
      next: (response) => this.handleSuccess(response),
      error: (error: HttpErrorResponse) => {
        this.errorMessage = this.getErrorMessage(error, 'Failed to clear notes. Please try again.');
        this.isWorking = false;
      }
    });
  }

  onCancel(): void {
    this.dialogRef.close(null);
  }

  private handleSuccess(response: AppendNoteResponseDto): void {
    this.isWorking = false;

    if (!response.success) {
      this.errorMessage = response.message || 'Request failed.';
      return;
    }

    this.dialogRef.close({ updatedNotes: response.updatedNotes ?? '' });
  }

  private getErrorMessage(error: HttpErrorResponse, fallback: string): string {
    return error?.error?.detail ?? error?.error?.title ?? error?.message ?? fallback;
  }
}
